/**
 * Composição dos modos do ADR-007.
 *
 * O resto do app fala com `VoiceEngine` e não sabe de onde vem o áudio. Aqui
 * é o único lugar que conhece as combinações:
 *
 *   hybrid   → escuta Realtime (cai para o navegador) + fala OmniVoice (Diego)
 *   realtime → escuta e fala pelo Realtime
 *   text     → escuta Realtime (cai para o navegador) + não fala
 *
 * A queda para o `listenBrowser` só existe em `hybrid` e `text`. Em `realtime`
 * o erro sobe: cair para o navegador ali trocaria a voz sem avisar, e o modo
 * `realtime` foi escolhido justamente pela voz e pela latência.
 */

import type { VoiceMode } from '@will/contracts';
import { createBrowserListener } from './listenBrowser';
import type { BrowserListenerOptions } from './listenBrowser';
import { createRealtimeListener } from './listenRealtime';
import type { RealtimeOptions, RealtimeSession } from './listenRealtime';
import { createOmnivoiceSpeaker, createSilentSpeaker } from './speakOmnivoice';
import type { OmnivoiceOptions } from './speakOmnivoice';
import type { HeardHandler, LevelHandler, Listener, Speaker, VoiceEngine } from './types';
import { comoErro } from './types';
import { relatar } from '../client/telemetry';

export interface VoiceEngineOptions {
  realtime?: Omit<RealtimeOptions, 'audioOutput'>;
  browser?: BrowserListenerOptions;
  omnivoice?: OmnivoiceOptions;
  /** Avisado quando o Realtime não sobe e a escuta cai para o navegador. */
  onFallback?: (motivo: Error) => void;
  /**
   * A pessoa falou por cima do Will.
   *
   * Só dispara quando quem detectou foi o OUVINTE — não quando o app chama
   * `hush()` de propósito. A distinção evita o laço óbvio: o app manda
   * interromper, o engine cala, o engine avisa o app, o app manda de novo.
   */
  onBargeIn?: () => void;
  onError?: (erro: Error) => void;
}

export function createVoiceEngine(
  mode: VoiceMode,
  opts: VoiceEngineOptions = {},
): VoiceEngine {
  const ouvintesTexto: HeardHandler[] = [];
  const ouvintesNivel: LevelHandler[] = [];

  let listener: Listener | null = null;
  let speaker: Speaker | null = null;
  let iniciando: Promise<void> | null = null;

  /** quando o Will começou a falar — o barge-in mede a partir daqui */
  let falandoDesde = 0;

  const hush = (): void => {
    // Quanto tempo o Will já estava falando quando foi interrompido. É a
    // medida que interessa ao full duplex: interromper depois de meia frase é
    // conversa; depois de três frases é o usuário desistindo de esperar.
    if (falandoDesde > 0) {
      relatar('voz.bargein', { ms: Date.now() - falandoDesde, detalhe: mode });
      falandoDesde = 0;
    }
    speaker?.hush();
    listener?.setTalking(false);
  };

  /**
   * Os callbacks do app são registrados antes do start(), mas o ouvinte só
   * nasce dentro dele — e pode ser trocado quando o Realtime falha. Por isso
   * o engine guarda as listas e reencaminha: quem registrou uma vez continua
   * ouvindo depois da queda.
   */
  const ligar = (l: Listener): void => {
    l.onHeard((texto, final) => {
      for (const cb of ouvintesTexto) cb(texto, final);
    });
    l.onLevel((nivel) => {
      for (const cb of ouvintesNivel) cb(nivel);
    });
    l.onBargeIn(() => {
      hush();
      // Calar o alto-falante é metade do barge-in. A outra metade é o servidor
      // parar de pensar e de gravar no histórico um turno que ninguém ouviu —
      // e ele só sabe disso se alguém contar.
      opts.onBargeIn?.();
    });
  };

  const criarRealtime = (audioOutput: boolean): RealtimeSession => {
    const sessao = createRealtimeListener({
      ...opts.realtime,
      audioOutput,
      onError: opts.onError,
    });
    ligar(sessao);
    return sessao;
  };

  // A voz existe desde a criação do engine, antes de qualquer gesto. Falar não
  // exige permissão do navegador nem microfone aberto — só ouvir exige. Enquanto
  // o speaker nascia dentro do start(), quem apenas digitava via o Will escrever
  // e nunca falar, porque sem clicar em "Ouvir" não havia engine nenhum.
  // Em `realtime` puro o speaker é a própria sessão e só existe após o start().
  if (mode === 'hybrid') {
    speaker = createOmnivoiceSpeaker({ ...opts.omnivoice, onError: opts.onError });
  } else if (mode === 'text') {
    speaker = createSilentSpeaker();
  }

  const iniciar = async (): Promise<void> => {
    if (mode === 'realtime') {
      const sessao = criarRealtime(true);
      await sessao.start(); // sem rede de segurança: `realtime` é tudo Realtime
      listener = sessao;
      speaker = sessao;
      relatar('voz.realtime.aberto', { detalhe: 'realtime' });
      return;
    }

    // A fala é armada ANTES da escuta, e isso não é estilo: falar e ouvir são
    // capacidades independentes, e a ordem anterior as acoplava. Se o Realtime
    // falhasse e o navegador também — permissão negada, Firefox, Safari — o
    // start() inteiro rejeitava e o `speaker` nunca era criado: o Will ficava
    // mudo junto, mesmo com o usuário ainda podendo digitar. Perder o microfone
    // é degradar; perder a voz por causa do microfone é quebrar.
    const sessao = criarRealtime(false);
    try {
      await sessao.start();
      listener = sessao;
      // A linha que eu precisava do console do Márcio, agora vindo sozinha.
      relatar('voz.realtime.aberto', { detalhe: mode });
      return;
    } catch (e) {
      sessao.stop();
      const causa = comoErro(e);
      relatar('voz.realtime.caiu', { detalhe: causa.message });
      opts.onFallback?.(causa);
    }

    // Segunda tentativa, agora pelo navegador. Se esta também falhar o erro
    // sobe para quem chamou, mas o `speaker` já está de pé — o Will continua
    // respondendo a turno digitado.
    const navegador = createBrowserListener({ ...opts.browser, onError: opts.onError });
    ligar(navegador);
    try {
      await navegador.start();
    } catch (e) {
      relatar('voz.microfone.negado', { detalhe: comoErro(e).message });
      throw e;
    }
    listener = navegador;
    relatar('voz.navegador.aberto', { detalhe: mode });
  };

  return {
    mode,

    start(): Promise<void> {
      // Um start() em voo por vez: dois cliques no botão de microfone não
      // podem abrir duas sessões Realtime e dois microfones.
      iniciando ??= iniciar().catch((e: unknown) => {
        iniciando = null;
        throw comoErro(e);
      });
      return iniciando;
    },

    /**
     * Para de ouvir. **Não** cala a voz: em `hybrid` e `text` o speaker
     * sobrevive, porque desligar o microfone é uma escolha sobre escutar, não
     * sobre responder. Em `realtime` o speaker É a sessão, então ele vai junto.
     */
    stop(): void {
      hush();
      listener?.stop();
      listener = null;
      iniciando = null;
      if (mode === 'realtime') speaker = null;
    },

    async speak(text: string, estado?: string): Promise<void> {
      if (!speaker) return; // modo `text`, ou fala antes do start()
      const alvo = listener;
      // O ouvinte precisa saber o texto para a guarda de eco funcionar; o
      // `finally` garante que ele volte a aceitar fala do usuário mesmo se o
      // TTS falhar no meio.
      alvo?.setTalking(true, text);
      falandoDesde = Date.now();
      try {
        await speaker.speak(text, estado);
      } finally {
        falandoDesde = 0;
        alvo?.setTalking(false);
      }
    },

    hush,

    onHeard(cb: HeardHandler): void {
      ouvintesTexto.push(cb);
    },

    onLevel(cb: LevelHandler): void {
      ouvintesNivel.push(cb);
    },
  };
}
